import {
  AlertTriangle,
  Ban,
  DollarSign,
  Fingerprint,
  MessageSquareOff,
  PenOff,
  Radio,
  ShieldAlert,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  UserMinus,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type {
  ModerationDashboardModuleDTO,
  ModerationMetricDTO,
  ModerationReportTypeCountDTO,
} from '../dashboardTypes';
import { DashboardBusy, NotReadyBadge, moderationReadyState } from './DashboardNotReady';
import { ConicDonut, formatModCount } from './dashboardChartPrimitives';

/**
 * Visual chrome for seeded {@code punishment_types.name} values
 * (tipmiDB V1_20260720_4 + V1_20260722_2). Layout follows Figma; catalog is real.
 */
const PENALTY_META: Record<string, { icon: LucideIcon; color: string }> = {
  warning: { icon: AlertTriangle, color: '#eab308' },
  posting_suspension: { icon: PenOff, color: '#f59e0b' },
  commenting_suspension: { icon: MessageSquareOff, color: '#a78bfa' },
  account_suspension: { icon: UserMinus, color: '#fb923c' },
  permanent_ban: { icon: Ban, color: '#f87171' },
  payout_pause: { icon: DollarSign, color: '#34d399' },
  demonetisation: { icon: ShieldAlert, color: '#f472b6' },
  age_verification: { icon: Fingerprint, color: '#60a5fa' },
  live_access_revocation: { icon: Radio, color: '#38bdf8' },
};

function metaFor(type: string | undefined, label: string): { icon: LucideIcon; color: string } {
  if (type && PENALTY_META[type]) return PENALTY_META[type];
  const lower = label.toLowerCase();
  for (const [code, meta] of Object.entries(PENALTY_META)) {
    if (lower.includes(code.replaceAll('_', ' ')) || lower.includes(code.split('_')[0]!)) {
      return meta;
    }
  }
  return { icon: AlertTriangle, color: '#94a3b8' };
}

function metricNum(m: ModerationMetricDTO | undefined): number | null {
  if (m?.value == null) return null;
  const n = typeof m.value === 'number' ? m.value : Number(m.value);
  return Number.isFinite(n) ? n : null;
}

function parseSourceCounts(note: string | undefined): { auto: number; human: number } | null {
  if (!note) return null;
  const match = note.match(/(\d+)\s+automated\s*\/\s*(\d+)\s+human/i);
  if (!match) return null;
  return { auto: Number(match[1]), human: Number(match[2]) };
}

/** Penalty Management: real punishment_types tiles + sources + active restrictions. */
export function PenaltyManagementFigmaCard({
  module,
  penaltyTypes,
  isLoading,
  className,
}: {
  module?: ModerationDashboardModuleDTO | undefined;
  penaltyTypes?: ModerationReportTypeCountDTO[] | undefined;
  isLoading?: boolean;
  className?: string;
}) {
  const state = moderationReadyState(module?.availability);
  const types = penaltyTypes ?? [];
  const byKey = Object.fromEntries((module?.metrics ?? []).map((m) => [m.key ?? '', m]));

  const autoPct = metricNum(byKey.PENALTY_SOURCE_AUTOMATED) ?? 0;
  const humanPct = Math.max(0, 100 - autoPct);
  const parsed = parseSourceCounts(byKey.PENALTY_SOURCE_AUTOMATED?.note);
  const autoCount = parsed?.auto ?? autoPct;
  const humanCount = parsed?.human ?? humanPct;
  const sourceTotal = autoCount + humanCount;

  return (
    <Card className={cn('border-border/40 bg-[#1a1a1c] shadow-none', className)}>
      <CardHeader className="space-y-3 p-5 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <h3 className="text-base font-semibold leading-6">
              {module?.label ?? 'Penalty Management'}
            </h3>
            {module?.note ? (
              <p className="text-xs text-muted-foreground">{module.note}</p>
            ) : null}
          </div>
          <NotReadyBadge state={state} {...(module?.note ? { hint: module.note } : {})} />
        </div>
      </CardHeader>
      <CardContent className="space-y-6 p-5 pt-0">
        {isLoading ? (
          <DashboardBusy className="h-48" />
        ) : state === 'not-ready' ? (
          <p className="text-sm text-muted-foreground">{module?.note ?? 'Not available yet.'}</p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-9">
              {types.length === 0 ? (
                <p className="col-span-full text-sm text-muted-foreground">
                  No penalty types configured.
                </p>
              ) : (
                types.map((t) => {
                  const label = t.label ?? t.type ?? 'Penalty';
                  const { icon: Icon, color } = metaFor(t.type, label);
                  const count = t.count ?? 0;
                  return (
                    <div
                      key={t.type ?? label}
                      className="flex flex-col items-center gap-2 rounded-xl border bg-[#141416] px-2 py-4 text-center"
                      style={{ borderColor: `${color}55` }}
                    >
                      <span
                        className="flex size-10 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${color}22`, color }}
                      >
                        <Icon className="size-5" aria-hidden />
                      </span>
                      <p className="line-clamp-2 min-h-[2rem] text-[11px] leading-tight text-muted-foreground">
                        {label}
                      </p>
                      <p className="text-2xl font-bold tabular-nums tracking-tight">
                        {formatModCount(count)}
                      </p>
                      <p className="text-xs font-medium tabular-nums" style={{ color }}>
                        {t.percentage != null ? `${Number(t.percentage).toFixed(1)}%` : '0.0%'}
                      </p>
                    </div>
                  );
                })
              )}
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-4">
                <p className="text-sm font-medium">Penalty Sources</p>
                <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-center">
                  <ConicDonut
                    sizeClass="size-36"
                    segments={[
                      {
                        label: 'Automated',
                        value: Math.max(sourceTotal > 0 ? autoCount : autoPct, 0.001),
                        color: '#a78bfa',
                      },
                      {
                        label: 'Human Review',
                        value: Math.max(sourceTotal > 0 ? humanCount : humanPct, 0.001),
                        color: '#3b82f6',
                      },
                    ]}
                    centerValue={sourceTotal > 0 ? formatModCount(sourceTotal) : `${autoPct}%`}
                    centerLabel={sourceTotal > 0 ? 'issued' : 'Auto'}
                  />
                  <div className="w-full flex-1 space-y-3">
                    <SourceBar
                      label="Automated"
                      pct={autoPct}
                      color="#a78bfa"
                      hint={
                        byKey.PENALTY_SOURCE_AUTOMATED?.note ??
                        'Share of penalties with no admin_id (proxy).'
                      }
                    />
                    <SourceBar label="Human Review" pct={humanPct} color="#3b82f6" />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium">Active Restrictions</p>
                <ul className="divide-y divide-border/30 overflow-hidden rounded-xl border border-border/40">
                  <RestrictionRow
                    icon={Users}
                    iconColor="#a78bfa"
                    metric={byKey.TOTAL_ACTIVE}
                    fallbackLabel="Total Active"
                    format={(v) => formatModCount(v)}
                  />
                  <RestrictionRow
                    icon={TrendingUp}
                    iconColor="#34d399"
                    metric={byKey.NEW_THIS_WEEK}
                    fallbackLabel="New This Week"
                    format={(v) => `+${formatModCount(v)}`}
                    valueClass="text-emerald-400"
                  />
                  <RestrictionRow
                    icon={TrendingDown}
                    iconColor="#f87171"
                    metric={byKey.RESOLVED_THIS_WEEK}
                    fallbackLabel="Resolved This Week"
                    format={(v) => `−${formatModCount(v)}`}
                    valueClass="text-rose-400"
                  />
                  <RestrictionRow
                    icon={ShieldCheck}
                    iconColor="#34d399"
                    metric={byKey.ENFORCEMENT_SUCCESS_RATE}
                    fallbackLabel="Enforcement Success Rate"
                    format={(v) => `${v}%`}
                  />
                </ul>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SourceBar({
  label,
  pct,
  color,
  hint,
}: {
  label: string;
  pct: number;
  color: string;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5" title={hint}>
      <div className="flex items-center justify-between text-sm">
        <span className="inline-flex items-center gap-2 text-muted-foreground">
          <span className="size-2.5 rounded-full" style={{ backgroundColor: color }} />
          {label}
        </span>
        <span className="font-semibold tabular-nums" style={{ color }}>
          {pct}%
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full"
          style={{ width: `${Math.min(100, Math.max(0, pct))}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

function RestrictionRow({
  icon: Icon,
  iconColor,
  metric,
  fallbackLabel,
  format,
  valueClass,
}: {
  icon: LucideIcon;
  iconColor: string;
  metric?: ModerationMetricDTO;
  fallbackLabel: string;
  format: (v: number) => string;
  valueClass?: string;
}) {
  const ready = moderationReadyState(metric?.availability);
  const live = ready === 'live' || ready === 'partial' || ready === 'snapshot';
  const val = metricNum(metric);

  return (
    <li className="flex items-center gap-3 px-3 py-3 text-sm">
      <span
        className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-white/5"
        style={{ color: iconColor }}
      >
        <Icon className="size-4" aria-hidden />
      </span>
      <span className="flex-1 text-muted-foreground">{metric?.label ?? fallbackLabel}</span>
      <span className={cn('font-semibold tabular-nums', valueClass)}>
        {live && val != null ? format(val) : '—'}
      </span>
      <NotReadyBadge state={ready} {...(metric?.note ? { hint: metric.note } : {})} />
    </li>
  );
}
